import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronRight } from 'lucide-react';
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ChatItem } from '../../../../contracts/harness.types';
import { isSectionLive } from '../../../../data/projects/timeline/presentation/derive-timeline-presentation';
import { deriveLineState } from '../../../../data/projects/timeline/working-line/lineState';
import type { WorkRow } from '../../../../data/projects/timeline/working-line/workRows';
import { ProjectThinkingIndicator } from '../../components/ProjectThinkingIndicator';
import { ShimmerText } from '../ShimmerText';
import { useWorkingRowText } from '../useWorkingRowText';
import { WORKING_TOGGLE_PRESS, WorkingDisclosure } from '../WorkingDisclosure';
import { EvidenceBadge } from './evidence/EvidenceBadge';
import { SourceAvatars, SourceCycle } from './evidence/EvidenceSources';
import { isEvidenceStep, type EvidenceTrails } from './evidence/evidenceTrail';
import { EvidenceWorkRow } from './evidence/EvidenceWorkRow';
import { useEvidenceTrail } from './evidence/useEvidenceTrail';
import { formatWorkingLine } from './workingBlockHeader';

const ATOM_ANIMATE_IN =
  'motion-safe:duration-300 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1';

const TICK_MS = 1000;
const NO_PHRASES: readonly string[] = [];

type DurationSource = 'live' | 'estimate';

type LiveSpanMs = { first: number; last: number };

type WorkingDurationInput = {
  isLive: boolean;
  liveSpanMs?: LiveSpanMs;
  estimatedSpan?: { startedAt: string; endedAt: string };
};

type WorkingDuration = {
  seconds: number | null;
  source: DurationSource | null;
};

type ServerAnchor = { clientMs: number; serverMs: number };

const msToSeconds = (ms: number): number => Math.max(0, Math.round(ms / 1000));

const spanSeconds = (startedAt: string, endedAt: string): number | null => {
  const started = Date.parse(startedAt);
  const ended = Date.parse(endedAt);
  if (!Number.isFinite(started) || !Number.isFinite(ended)) return null;
  return msToSeconds(ended - started);
};

const resolveWorkingDuration = (
  input: WorkingDurationInput,
  now: number,
  anchor: ServerAnchor | null
): WorkingDuration => {
  const { isLive, liveSpanMs, estimatedSpan } = input;

  if (isLive) {
    if (anchor === null) return { seconds: 0, source: 'live' };
    const sinceAnchor = now - anchor.clientMs;
    return {
      seconds: msToSeconds(
        liveSpanMs === undefined
          ? sinceAnchor
          : sinceAnchor + (anchor.serverMs - liveSpanMs.first)
      ),
      source: 'live',
    };
  }

  if (liveSpanMs !== undefined) {
    return { seconds: msToSeconds(liveSpanMs.last - liveSpanMs.first), source: 'live' };
  }
  if (estimatedSpan) {
    const seconds = spanSeconds(estimatedSpan.startedAt, estimatedSpan.endedAt);
    if (seconds !== null) return { seconds, source: 'estimate' };
  }
  return { seconds: null, source: null };
};

/**
 * Settled: the frozen server span, else the last live tick (an identity-less
 * stream never had a span), else the reload estimate.
 */
const resolveSettledDuration = (
  input: WorkingDurationInput,
  lastReading: WorkingDuration
): WorkingDuration => {
  if (input.liveSpanMs !== undefined) {
    return resolveWorkingDuration(input, Date.now(), null);
  }
  if (lastReading.source === 'live') return lastReading;
  return resolveWorkingDuration(input, Date.now(), null);
};

/**
 * Live duration is server-anchored when the span is known and ticks in an
 * effect. The settled reading is derived during render, so the first settled
 * frame already carries the final duration — a line decided against the
 * threshold never flips one render later.
 */
const useWorkingDuration = ({
  isLive,
  liveSpanMs,
  estimatedSpan,
}: WorkingDurationInput): WorkingDuration => {
  const [reading, setReading] = useState<WorkingDuration>({
    seconds: null,
    source: null,
  });
  const serverAnchorRef = useRef<ServerAnchor | null>(null);

  const liveFirst = liveSpanMs?.first;
  const liveLast = liveSpanMs?.last;
  const estimatedStartedAt = estimatedSpan?.startedAt;
  const estimatedEndedAt = estimatedSpan?.endedAt;

  useEffect(() => {
    const input: WorkingDurationInput = {
      isLive,
      ...(liveFirst !== undefined && liveLast !== undefined
        ? { liveSpanMs: { first: liveFirst, last: liveLast } }
        : {}),
      ...(estimatedStartedAt !== undefined && estimatedEndedAt !== undefined
        ? { estimatedSpan: { startedAt: estimatedStartedAt, endedAt: estimatedEndedAt } }
        : {}),
    };

    // Pair the newest server frame with the client clock; only ever move forward.
    const advanceAnchor = (now: number): ServerAnchor | null => {
      if (liveLast === undefined) {
        serverAnchorRef.current ??= { clientMs: now, serverMs: now };
        return serverAnchorRef.current;
      }
      const anchor = serverAnchorRef.current;
      if (anchor === null || liveLast > anchor.serverMs) {
        serverAnchorRef.current = { clientMs: now, serverMs: liveLast };
      }
      return serverAnchorRef.current;
    };

    if (!isLive) return;

    const tick = () => {
      const now = Date.now();
      setReading(resolveWorkingDuration(input, now, advanceAnchor(now)));
    };
    tick();
    const id = setInterval(tick, TICK_MS);
    return () => clearInterval(id);
  }, [isLive, liveFirst, liveLast, estimatedStartedAt, estimatedEndedAt]);

  if (isLive) return reading;
  return resolveSettledDuration(
    {
      isLive,
      ...(liveSpanMs ? { liveSpanMs } : {}),
      ...(estimatedSpan ? { estimatedSpan } : {}),
    },
    reading
  );
};

export interface WorkingBlockProps {
  /** Consecutive non-answer items this block owns, in timeline order. */
  members: ChatItem[];
  /** True while the turn is still running. */
  turnInFlight: boolean;
  /**
   * Nothing has followed this section yet; with the turn in flight it is still
   * live.
   */
  openEnded?: boolean;
  /** Renders one member with the normal per-item presentation. */
  renderMember: (item: ChatItem) => ReactNode;
  liveSpanMs?: LiveSpanMs;
  estimatedSpan?: { startedAt: string; endedAt: string };
  /** The rows the line names and counts (`deriveWorkRows`). */
  rows: WorkRow[];
  /** What ended the run; only an answer recedes the work. */
  closedBy: 'answer' | 'other' | null;
  /** Where each Evidence step's searches and sources are read; Evidence's data is app-side. */
  evidence?: EvidenceTrails;
  /** The turn's contextual loader phrases, rotated in the line while it has nothing to name. */
  phrases?: readonly string[];
}

/**
 * Collapsible Working block: a run of thinking steps and tool rows behind one
 * line. Live, the line names the work in flight; settled, it says what the
 * block did. It never opens itself — expansion is the clinician's — and it
 * recedes only when an answer closes it, so work that ended without one stays
 * as the clinician left it.
 *
 * Styling follows the shipped `GroupedToolUseBlock` rather than the demo's
 * markup. The block folds (`WorkingDisclosure`) on the design system's
 * collapsible, and its rows unmount once it is shut.
 */
export const WorkingBlock = ({
  members,
  turnInFlight,
  openEnded,
  renderMember,
  liveSpanMs,
  estimatedSpan,
  rows,
  closedBy,
  evidence,
  phrases = NO_PHRASES,
}: WorkingBlockProps) => {
  const intl = useIntl();
  const isLive = isSectionLive(members, turnInFlight, openEnded);

  const [expanded, setExpanded] = useState(false);
  const rowText = useWorkingRowText();
  const contentId = useId();
  const { seconds: durationSeconds, source } = useWorkingDuration({
    isLive,
    liveSpanMs,
    estimatedSpan,
  });

  useEffect(() => {
    // `isLive` only ever falls, so this fires on the settling edge and a later
    // manual re-expand sticks.
    if (!isLive && closedBy === 'answer') setExpanded(false);
  }, [isLive, closedBy]);

  const [only] = rows;
  // A settled block of one Evidence step reports what it found.
  const onlyTrail = useEvidenceTrail(
    evidence,
    !isLive && rows.length === 1 && only && isEvidenceStep(only.item) ? only.item : null
  );
  const line = deriveLineState({
    rows,
    live: isLive,
    open: expanded,
    seconds: durationSeconds,
    yieldCount: onlyTrail?.found?.length ?? null,
    phrases,
  });
  const header = formatWorkingLine(intl, line);
  const namesEvidence =
    line.kind === 'row' && isEvidenceStep(line.row.item) ? line : undefined;
  const liveTrail = useEvidenceTrail(
    evidence,
    namesEvidence?.tense === 'active' ? namesEvidence.row.item : null
  );
  const yielded =
    line.kind === 'settled' && line.summary.form === 'yield' ? onlyTrail?.found : null;

  return (
    <div
      className={cn('flex w-full min-w-0 flex-col', isLive && ATOM_ANIMATE_IN)}
      data-testid="project-working-block"
      data-duration-source={source ?? undefined}
    >
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded((open) => !open)}
        className={cn(
          'group/working flex min-w-0 items-center gap-1.5 self-start text-start',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
          WORKING_TOGGLE_PRESS
        )}
      >
        {line.kind === 'phrases' ? (
          <ProjectThinkingIndicator messages={line.phrases} />
        ) : (
          <TypographyP2 className={cn(rowText, 'text-text-secondary')}>
            {isLive ? <ShimmerText className="inline">{header}</ShimmerText> : header}
          </TypographyP2>
        )}
        {namesEvidence ? <EvidenceBadge /> : null}
        {liveTrail ? <SourceCycle sources={liveTrail.returned} /> : null}
        {yielded ? <SourceAvatars sources={yielded} /> : null}
        <ChevronRight
          className={cn(
            'size-3.5 shrink-0 text-text-tertiary rtl:-scale-x-100',
            'motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out-quart motion-reduce:transition-none',
            'opacity-60 group-hover/working:opacity-100 group-focus-visible/working:opacity-100',
            expanded && 'rotate-90'
          )}
          aria-hidden
        />
      </button>

      <WorkingDisclosure
        id={contentId}
        open={expanded}
        className="flex flex-col gap-3 pt-2"
        data-testid="project-working-block-content"
      >
        {members.map((member) => (
          // Fragment, not a wrapper: members that render nothing must not
          // leave an empty box behind in the gap-3 column.
          <Fragment key={member.id}>
            {isEvidenceStep(member) ? (
              <EvidenceWorkRow item={member} trails={evidence} />
            ) : (
              renderMember(member)
            )}
          </Fragment>
        ))}
      </WorkingDisclosure>
    </div>
  );
};
